<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import { memberGet } from "@/common/api/rest/member";
import {
	accountShowByMember,
	accountStatus,
} from "@/common/api/rest/account/account";

import type { MemberPayloadType } from "@/common/types/member";

import {
	baseAvatar,
	baseInput,
	baseSelect,
	baseTextarea,
	breadcrumbSimple,
	tabMenu,
} from "@/common/components";

type TabActiveType = "edit" | "cv";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const member = ref<any>(null);
const account = ref<any>(null);

const memberId = computed(() => route.params.member as string);

const breadcrumbList = computed(() => [
	{
		key: "members",
		label: t("common.labels.members"),
		url: "/members",
	},
	{
		key: "member",
		label: member.value?.name || t("common.labels.member"),
	},
	{
		key: "edit",
		label: t("common.labels.edit"),
	},
]);

const tabList = computed(() => [
	{
		key: "edit",
		label: t("common.labels.member").toUpperCase(),
	},
	{
		key: "account",
		label: t("common.labels.account").toUpperCase(),
	},
	{
		key: "affiliate",
		label: "AFILIADOS",
	},
]);

const activeOptions = computed(() => [
	{ option: "ATIVO", value: "ACTIVE" },
	{ option: "INATIVO", value: "INACTIVE" },
	{ option: "CANCELADO", value: "CANCELED" },
	{ option: "RESTRITO", value: "RESTRICTED" },
	{ option: "EM ATRASO", value: "PAST_DUE" },
]);

function fetchData() {
	memberGet(memberId.value)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				member.value = data;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});

	accountShowByMember(memberId.value)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				account.value = data;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function updateAccount() {
	accountStatus(account.value.account_id, account.value.status)
		.then((response) => {
			const { success, message } = response;

			if (success) {
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function selectTab(tab: TabActiveType) {
	router.push(`/members/${memberId.value}/${tab}`);
}

onMounted(() => {
	fetchData();
});
</script>

<template>
	<article class="member-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="edit"
			/>
		</section>

		<section class="page-content">
			<tab-menu
				:tabs="tabList"
				:tooltip="false"
				active="account"
				width="fit-content"
				padding-inline="1.5rem"
				@select:tab="selectTab"
			>
				<template #content>
					<template v-if="account">
						<div class="member-form">
							<base-input
								v-model="account.name"
								:value="account.name"
								:placeholder="t('common.placeholders.name')"
								:required="true"
								:max-length="75"
								:disabled="true"
							>
								<template #label> {{ t("common.labels.name") }} * </template>
							</base-input>

							<base-select
								v-model="account.status"
								:value="account.status"
								:options="activeOptions"
								:is-empty="false"
								@change="updateAccount()"
							>
								<template #label> {{ t("common.labels.status") }}</template>
							</base-select>

							<base-textarea
								v-model="account.description"
								:value="account.description"
								:rows="5"
								:disabled="true"
							>
								<template #label>
									{{ t("common.labels.description") }}
								</template>
							</base-textarea>
						</div>
					</template>
				</template>
			</tab-menu>
		</section>
	</article>
</template>

<style lang="scss" scoped>
.member-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		margin-block-end: 1.54rem;

		.member-form {
			display: flex;
			flex-direction: column;
			gap: 1rem;
		}
	}
}
</style>
